import React from 'react';

import { View, Text, Image } from '@react-pdf/renderer';

import { FAMILIA_SERIFA, FAMILIA_SERIFA_NEGRITO } from '../assets/fontesDanfe';
import { CORES, FIO, larguraRestante } from './grade';
import { ENTRELINHA_NATURAL } from './metricaTexto';

/**
 * @file ./views/layouts/danfe/producao/primitivos.jsx
 *
 * Tradução das duas primitivas de desenho do `pdfkit` para o
 * `@react-pdf/renderer`, preservando a semântica de coordenada absoluta:
 *
 * | pdfkit                                  | aqui              |
 * | --------------------------------------- | ----------------- |
 * | `doc.rect(x, y, l, a).stroke()`         | `<Quadro>`        |
 * | `doc.rect(...).fillAndStroke(cor, fio)` | `<Quadro fundo>`  |
 * | `doc.text(s, x, y, opcoes)`             | `<Texto>`         |
 * | `doc.image(buf, x, y, { width, height })` | `<Imagem>`      |
 * | `doc.moveTo/lineTo/dash/stroke`         | `<LinhaDeRecorte>`|
 *
 * A `<Page>` que hospeda estes componentes precisa ter `padding: 0`: no
 * pdfkit as coordenadas de `rect` são medidas a partir da origem da página
 * e ignoram as margens, que só afetam o cursor de texto em fluxo. Qualquer
 * padding na página deslocaria o documento inteiro.
 */

/** Mapeia opções do pdfkit para estilo do react-pdf. */
function estiloTexto({
  x,
  y,
  largura,
  corpo,
  espacamento,
  negrito,
  alinhamento,
  cor,
  entrelinha,
}) {
  return {
    position: 'absolute',
    left: x,
    top: y,
    // Sem `width`, o pdfkit assume daqui até a margem direita. Explicitar
    // é o que faz `align: 'center'` cair no mesmo lugar dos dois lados.
    width: largura ?? larguraRestante(x),
    // As base-14 do PDF são famílias distintas, não pesos de uma mesma
    // família: `fontWeight: 'bold'` sobre `Times-Roman` não resolve para
    // `Times-Bold`. O negrito é escolhido trocando a família.
    fontFamily: negrito ? FAMILIA_SERIFA_NEGRITO : FAMILIA_SERIFA,
    fontSize: corpo,
    color: cor ?? CORES.tinta,
    textAlign: alinhamento ?? 'left',
    letterSpacing: espacamento,
    // `lineGap` do pdfkit é espaço adicional em pontos; o react-pdf
    // trabalha com multiplicador do corpo.
    lineHeight: entrelinha
      ? ENTRELINHA_NATURAL + entrelinha / corpo
      : ENTRELINHA_NATURAL,
  };
}

/**
 * Moldura retangular. Equivalente a `doc.rect(x, y, l, a).stroke()`, ou a
 * `fillAndStroke` quando `fundo` é informado.
 *
 * @param {Object} props
 * @param {number} props.x
 * @param {number} props.y
 * @param {number} props.largura
 * @param {number} props.altura
 * @param {string} [props.fundo] - Cor de preenchimento.
 * @returns {React.ReactElement}
 */
export function Quadro({ x, y, largura, altura, fundo }) {
  return (
    <View
      style={{
        position: 'absolute',
        left: x,
        top: y,
        width: largura,
        height: altura,
        borderWidth: FIO.espessura,
        borderStyle: 'solid',
        borderColor: FIO.cor,
        borderRadius: FIO.raio,
        backgroundColor: fundo,
      }}
    />
  );
}

/**
 * Texto em coordenada absoluta. Equivalente a `doc.text(s, x, y, opcoes)`.
 *
 * Quando `altura` é informada, o texto é embrulhado num contêiner que
 * recorta o excedente — o mesmo efeito da opção `height` do pdfkit, usada
 * nos campos de informações complementares.
 *
 * @param {Object} props
 * @param {number} props.x
 * @param {number} props.y
 * @param {number} [props.largura] - Padrão: daqui até a margem direita.
 * @param {number} props.corpo - Corpo da fonte em pt.
 * @param {number} [props.espacamento] - `characterSpacing` do pdfkit.
 * @param {boolean} [props.negrito=false]
 * @param {'left'|'center'|'right'} [props.alinhamento='left']
 * @param {string} [props.cor]
 * @param {number} [props.entrelinha] - `lineGap` do pdfkit, em pt.
 * @param {number} [props.altura] - Recorta o que passar desta altura.
 * @param {React.ReactNode} props.children
 * @returns {React.ReactElement|null}
 */
export function Texto({
  x,
  y,
  largura,
  corpo,
  espacamento,
  negrito = false,
  alinhamento,
  cor,
  entrelinha,
  altura,
  children,
}) {
  if (children === undefined || children === null || children === '')
    return null;

  const estilo = estiloTexto({
    x,
    y,
    largura,
    corpo,
    espacamento,
    negrito,
    alinhamento,
    cor,
    entrelinha,
  });

  if (altura === undefined) {
    return <Text style={estilo}>{children}</Text>;
  }

  const { position, left, top, width, ...tipografia } = estilo;

  return (
    <View
      style={{ position, left, top, width, height: altura, overflow: 'hidden' }}
    >
      <Text style={{ ...tipografia, width }}>{children}</Text>
    </View>
  );
}

/**
 * Campo comum: moldura, rótulo em 5 pt e valor em 8 pt — a tripla que se
 * repete dezenas de vezes no desenho original.
 *
 * @param {Object} props
 * @param {Object} props.grade - Entrada de `grade.js` com `caixa`, `rotulo` e `valor`.
 * @param {string} props.rotulo - Texto do rótulo, em caixa alta.
 * @param {string|number} [props.valor] - Conteúdo já formatado pelo mapper.
 * @param {React.ReactNode} [props.children] - Substitui o texto do valor.
 * @returns {React.ReactElement}
 */
export function Campo({ grade, rotulo, valor, children }) {
  return (
    <>
      <Quadro {...grade.caixa} fundo={grade.fundo} />
      <Texto {...grade.rotulo}>{rotulo}</Texto>
      {children ?? <Texto {...grade.valor}>{valor}</Texto>}
    </>
  );
}

/**
 * Imagem em coordenada absoluta — equivalente a
 * `doc.image(buffer, x, y, { width, height })`.
 *
 * @param {Object} props
 * @param {string} props.src - Data URI da imagem.
 * @param {number} props.x
 * @param {number} props.y
 * @param {number} props.largura
 * @param {number} props.altura
 * @returns {React.ReactElement}
 */
export function Imagem({ src, x, y, largura, altura }) {
  return (
    <Image
      src={src}
      cache
      style={{
        position: 'absolute',
        left: x,
        top: y,
        width: largura,
        height: altura,
        objectFit: 'fill',
      }}
    />
  );
}

/**
 * Fio de recorte tracejado do canhoto — equivalente ao par
 * `doc.dash(1.5, { space: 1.5 })` / `undash()` do original.
 *
 * @param {Object} props
 * @param {number} props.x
 * @param {number} props.y
 * @param {number} props.largura
 * @returns {React.ReactElement}
 */
export function LinhaDeRecorte({ x, y, largura }) {
  return (
    <View
      style={{
        position: 'absolute',
        left: x,
        top: y,
        width: largura,
        borderTopWidth: 1,
        borderTopStyle: 'dashed',
        borderTopColor: CORES.tinta,
      }}
    />
  );
}
